/**
 * ScalingUpDashboard Component
 * Orchestrator for the Scaling Up strategic dashboard tab.
 * Fetches data via useScalingUp hook and renders 6 sub-components.
 */

import { useState, useCallback } from 'react';
import { useScalingUp } from '@/hooks/useScalingUp';
import { useTrendInsights } from '@/hooks/useTrendInsights';
import { useMonthEvents } from '@/hooks/useMonthEvents';
import { SimpleNumbersPnl } from './SimpleNumbersPnl';
import { CriticalNumberGauge } from './CriticalNumberGauge';
import { CashConversionCycleCard } from './CashConversionCycleCard';
import { DlerCard } from './DlerCard';
import { PerFteCard } from './PerFteCard';
import { PowerOfOneSimulator } from './PowerOfOneSimulator';
import { ScalingUpTrends } from './ScalingUpTrends';
import { ChartInsightPanel } from './ChartInsightPanel';
import { PageNotes } from './PageNotes';
import type { OlympicBest, TrendInsight } from '@/types/scalingUp';
import './ScalingUpDashboard.css';

// Map from metric key to insight metric name (matches AI-generated labels)
const INSIGHT_SEARCH: Record<OlympicBest['metric'], string> = {
  revenue: 'revenue',
  grossMarginPercent: 'gross',
  dler: 'dler',
  cccDays: 'ccc',
  cashRunwayMonths: 'cash',
  revenuePerEmployee: 'per fte',
};

const METRIC_TITLES: Record<OlympicBest['metric'], string> = {
  revenue: 'Revenue',
  grossMarginPercent: 'Gross Margin %',
  dler: 'DLER',
  cccDays: 'CCC Days',
  cashRunwayMonths: 'Cash Runway',
  revenuePerEmployee: 'Revenue per FTE',
};

function findInsight(insights: TrendInsight[], key: OlympicBest['metric']): TrendInsight | null {
  const term = INSIGHT_SEARCH[key];
  if (key === 'revenue') {
    return insights.find((i) => {
      const m = i.metric.toLowerCase();
      return m.includes('revenue') && !m.includes('per') && !m.includes('fte');
    }) ?? null;
  }
  return insights.find((i) => i.metric.toLowerCase().includes(term)) ?? null;
}

export function ScalingUpDashboard() {
  const { data, loading, error, refresh } = useScalingUp();
  const { data: insights, fetchInsights, loading: insightsLoading } = useTrendInsights();
  const { events } = useMonthEvents();
  const [insightMetric, setInsightMetric] = useState<OlympicBest['metric'] | null>(null);

  const handleOpenInsight = useCallback((metric: OlympicBest['metric']) => {
    setInsightMetric(metric);
    if (!insights && !insightsLoading) void fetchInsights();
  }, [insights, insightsLoading, fetchInsights]);

  const handleCloseInsight = useCallback(() => setInsightMetric(null), []);

  const handleRefreshInsights = useCallback(() => { void fetchInsights(true); }, [fetchInsights]);

  if (error) {
    return (
      <div className="dashboard-error" role="alert" aria-live="assertive">
        <div className="dashboard-error__icon">&#9888;</div>
        <h2 className="dashboard-error__title">Unable to load Scaling Up</h2>
        <p className="dashboard-error__message">{error}</p>
        <button onClick={refresh} className="dashboard-error__button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="scaling-up">
      <header className="scaling-up__header">
        <div>
          <h1 className="scaling-up__title">Scaling Up</h1>
          <p className="scaling-up__subtitle">
            Strategic metrics &mdash; Verne Harnish framework
          </p>
        </div>
        <button
          onClick={refresh}
          className="scaling-up__refresh"
          disabled={loading}
          aria-label="Refresh Scaling Up data"
        >
          <span className="scaling-up__refresh-icon">&#8635;</span>
          Refresh
        </button>
      </header>

      {/* Row 1: Critical Number + Simple Numbers P&L */}
      <div className="scaling-up__row scaling-up__row--top">
        <CriticalNumberGauge
          data={data?.cashRunway ?? null}
          loading={loading}
          onInsight={() => handleOpenInsight('cashRunwayMonths')}
        />
        <SimpleNumbersPnl
          data={data?.simpleNumbers ?? null}
          loading={loading}
          onInsight={() => handleOpenInsight('grossMarginPercent')}
        />
      </div>

      {/* Row 2: CCC + DLER + Per-FTE */}
      <div className="scaling-up__row scaling-up__row--metrics">
        <CashConversionCycleCard
          data={data?.cashConversionCycle ?? null}
          loading={loading}
          onInsight={() => handleOpenInsight('cccDays')}
        />
        <DlerCard
          data={data?.dler ?? null}
          loading={loading}
          onInsight={() => handleOpenInsight('dler')}
        />
        <PerFteCard
          data={data?.perFte ?? null}
          loading={loading}
          onInsight={() => handleOpenInsight('revenuePerEmployee')}
        />
      </div>

      {/* Row 3: Power of One Simulator */}
      {data?.powerOfOneBaseline && (
        <PowerOfOneSimulator baseline={data.powerOfOneBaseline} />
      )}
      {loading && !data && (
        <div className="scaling-up__skeleton" style={{ height: '300px' }} />
      )}

      <ScalingUpTrends />

      <PageNotes page="scaling-up" title="Scaling Up Notes" />

      {insightMetric && (
        <ChartInsightPanel
          metricKey={insightMetric}
          metricTitle={METRIC_TITLES[insightMetric]}
          insight={findInsight(insights?.insights ?? [], insightMetric)}
          loading={insightsLoading}
          generatedAt={insights?.generatedAt ?? null}
          events={events}
          onClose={handleCloseInsight}
          onRefresh={handleRefreshInsights}
        />
      )}
    </div>
  );
}
